import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import {
  FileText,
  Upload,
  Scissors,
  Move,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Check,
  X,
  Loader2,
  Square,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Image as ImageIcon
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

// Set worker path for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * PDFComposerDialog - Import et découpe de PDF
 * Permet de charger un PDF, naviguer entre les pages, et découper des zones
 */

export function PDFComposerDialog({ 
  open, 
  onClose, 
  onInsertCrop 
}) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageImage, setPageImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  
  // Selection state for cropping
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selection, setSelection] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load PDF file
  const loadPDF = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      setSelection(null);
    } catch (err) {
      console.error("PDF load error:", err);
      setError("Impossible de charger le PDF. Vérifiez que le fichier est valide.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Render current page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    
    setLoading(true);
    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.5 * zoom });
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: ctx,
        viewport: viewport,
      }).promise;
      
      setPageImage(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Page render error:", err);
      setError("Erreur lors du rendu de la page.");
    } finally {
      setLoading(false);
    }
  }, [pdfDoc, currentPage, zoom]);

  // Render page when it changes
  useEffect(() => {
    if (pdfDoc) {
      renderPage();
    }
  }, [pdfDoc, currentPage, zoom, renderPage]);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type === "application/pdf") {
      loadPDF(file);
    }
  }, [loadPDF]);

  // Handle file input
  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      loadPDF(file);
    }
  };

  // Mouse handlers for selection
  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsSelecting(true);
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
    setSelection(null);
  };

  const handleMouseMove = (e) => {
    if (!isSelecting || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const y = Math.min(Math.max(0, e.clientY - rect.top), rect.height);
    
    setSelectionEnd({ x, y });
  };

  const handleMouseUp = () => {
    if (!isSelecting || !selectionStart || !selectionEnd) return;
    
    const x = Math.min(selectionStart.x, selectionEnd.x);
    const y = Math.min(selectionStart.y, selectionEnd.y);
    const width = Math.abs(selectionEnd.x - selectionStart.x);
    const height = Math.abs(selectionEnd.y - selectionStart.y);
    
    if (width > 10 && height > 10) {
      setSelection({ x, y, width, height });
    }
    
    setIsSelecting(false);
  };

  // Crop and insert selection
  const handleInsertCrop = () => {
    if (!selection || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const cropCanvas = document.createElement("canvas");
    const ctx = cropCanvas.getContext("2d");
    
    cropCanvas.width = selection.width;
    cropCanvas.height = selection.height;
    
    ctx.drawImage(
      canvas,
      selection.x, selection.y, selection.width, selection.height,
      0, 0, selection.width, selection.height
    );
    
    const croppedImage = cropCanvas.toDataURL("image/png");
    
    onInsertCrop?.({
      src: croppedImage,
      width: selection.width,
      height: selection.height,
      sourceFile: "PDF Import",
      sourcePage: currentPage,
    });
    
    onClose();
  };

  // Insert full page
  const handleInsertFullPage = () => {
    if (!pageImage || !canvasRef.current) return;
    
    onInsertCrop?.({
      src: pageImage,
      width: canvasRef.current.width / (1.5 * zoom),
      height: canvasRef.current.height / (1.5 * zoom),
      sourceFile: "PDF Import",
      sourcePage: currentPage,
    });
    
    onClose();
  };

  // Calculate selection rect for overlay
  const getSelectionStyle = () => {
    if (!selectionStart || !selectionEnd) return null;
    
    const x = Math.min(selectionStart.x, selectionEnd.x);
    const y = Math.min(selectionStart.y, selectionEnd.y);
    const width = Math.abs(selectionEnd.x - selectionStart.x);
    const height = Math.abs(selectionEnd.y - selectionStart.y);
    
    return {
      left: x,
      top: y,
      width,
      height,
    };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-500" />
            Composer PDF
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {!pdfDoc ? (
            // Upload area
            <div
              className="flex-1 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center p-8 transition-colors hover:border-indigo-400 cursor-pointer"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                className="hidden"
              />
              
              {loading ? (
                <>
                  <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
                  <p className="text-slate-600">Chargement du PDF...</p>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mb-4" />
                  <p className="font-medium text-slate-700 mb-2">
                    Glissez un PDF ici ou cliquez pour sélectionner
                  </p>
                  <p className="text-sm text-slate-500">
                    Formats acceptés : PDF
                  </p>
                </>
              )}
              
              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}
            </div>
          ) : (
            // PDF viewer with crop
            <div className="flex-1 flex flex-col min-h-0">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                    disabled={zoom <= 0.5}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600 w-16 text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setZoom(Math.min(2, zoom + 0.25))}
                    disabled={zoom >= 2}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPdfDoc(null);
                      setPageImage(null);
                      setSelection(null);
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Nouveau PDF
                  </Button>
                </div>
              </div>

              {/* Canvas container */}
              <ScrollArea className="flex-1 bg-slate-100 rounded-lg">
                <div 
                  ref={containerRef}
                  className="p-4 flex items-center justify-center min-h-full"
                >
                  <div 
                    className="relative bg-white shadow-lg"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <canvas
                      ref={canvasRef}
                      className="cursor-crosshair"
                    />
                    
                    {/* Selection overlay */}
                    {(isSelecting || selection) && (
                      <div
                        className="absolute border-2 border-indigo-500 bg-indigo-500/20 pointer-events-none"
                        style={selection ? {
                          left: selection.x,
                          top: selection.y,
                          width: selection.width,
                          height: selection.height,
                        } : getSelectionStyle()}
                      >
                        {selection && (
                          <div className="absolute -top-8 left-0 bg-indigo-500 text-white text-xs px-2 py-1 rounded">
                            {Math.round(selection.width)} × {Math.round(selection.height)} px
                          </div>
                        )}
                      </div>
                    )}
                    
                    {loading && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>

              {/* Instructions */}
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Scissors className="w-4 h-4 text-indigo-500" />
                  <span>
                    <strong>Découpe :</strong> Cliquez et faites glisser pour sélectionner une zone
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {pdfDoc && (
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleInsertFullPage}
                disabled={!pageImage}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Insérer la page entière
              </Button>
              <Button
                onClick={handleInsertCrop}
                disabled={!selection}
              >
                <Scissors className="w-4 h-4 mr-2" />
                Insérer la sélection
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PDFComposerDialog;
